import { BellRing, RefreshCw, WifiOff, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { enablePush, usePushState, useOnline } from '../lib/pwa.ts';
import { Button } from './ui/button.tsx';
import { useToast } from './ui/toast.tsx';

/**
 * App-level banners: offline notice and "new version available".
 * Also lets a tapped push notification move an already-open window to
 * the right ticket (the service worker posts a `navigate` message).
 */
export function AppStatus() {
  const online = useOnline();
  const navigate = useNavigate();
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, reg) {
      // Staff keep the app open for days: check for a new version hourly.
      if (reg) setInterval(() => void reg.update(), 60 * 60_000);
    },
  });

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type === 'navigate' && typeof e.data.url === 'string' && e.data.url.startsWith('/')) {
        navigate(e.data.url);
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  return (
    <>
      {!online && (
        <div
          role="status"
          className="flex items-center justify-center gap-2 bg-warning-soft px-4 py-2 text-sm text-warning"
        >
          <WifiOff className="size-4 shrink-0" aria-hidden />
          Can’t reach the helpdesk right now. You can look around; sending tickets and replies needs a
          connection.
        </div>
      )}
      {needRefresh && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-center gap-3 bg-info-soft px-4 py-2 text-sm text-info"
        >
          <span className="flex items-center gap-2">
            <RefreshCw className="size-4 shrink-0" aria-hidden />A new version of the helpdesk is ready.
          </span>
          <span className="flex gap-2">
            <Button size="sm" onClick={() => void updateServiceWorker(true)}>
              Reload
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setNeedRefresh(false)}>
              Later
            </Button>
          </span>
        </div>
      )}
    </>
  );
}

const NUDGE_KEY = 'helpdesk.pushNudgeDismissed';

/**
 * A one-time suggestion to turn on push notifications, shown on the ticket
 * lists until the person turns them on or says "Not now".
 */
export function PushNudge({ text }: { text: string }) {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(NUDGE_KEY) === '1';
    } catch {
      return true;
    }
  });
  const [state, setState] = usePushState(!dismissed);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  if (dismissed || state !== 'off') return null;

  const dismiss = () => {
    try {
      localStorage.setItem(NUDGE_KEY, '1');
    } catch {
      // Not persisted in private browsing; it just shows again next time.
    }
    setDismissed(true);
  };

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-brand/30 bg-brand-soft/60 p-4">
      <BellRing className="size-5 shrink-0 text-brand" aria-hidden />
      <p className="min-w-48 flex-1 text-sm">{text}</p>
      <div className="flex gap-2">
        <Button
          size="sm"
          loading={busy}
          onClick={async () => {
            setBusy(true);
            try {
              const next = await enablePush();
              setState(next);
              if (next === 'on') toast({ tone: 'success', title: 'Notifications are on for this device' });
            } catch {
              toast({
                tone: 'error',
                title: 'Couldn’t turn on notifications',
                body: 'Try again from Settings.',
              });
            } finally {
              setBusy(false);
            }
          }}
        >
          Turn on
        </Button>
        <Button size="sm" variant="ghost" onClick={dismiss} aria-label="Not now">
          <X className="size-4 sm:hidden" aria-hidden />
          <span className="hidden sm:inline">Not now</span>
        </Button>
      </div>
    </div>
  );
}
